import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import * as React from 'react';
import { Send } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { CommentList } from '@/components/domains/comment/CommentList';
import { useTranslations } from 'next-intl';
import { useForm } from 'react-hook-form';
import { CommentCreateInput, commentCreateSchema } from '@/models/schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { Comment } from '@/types/comment';
import { useSession } from 'next-auth/react';
import Avatar from '@/components/misc/Avatar';
import { MentionAutocomplete } from '@/components/mentions/MentionAutocomplete';
import { MentionUser } from '@/types/notification';
import { MentionHighlighter } from '@/components/mentions/MentionHighlighter';
import { useMentions } from '@/hooks/use-mentions';

interface CommentBoxProps {
  comments: Comment[];
  addComment: (comment: CommentCreateInput) => Promise<void>;
  removeComment?: (commentId: string) => Promise<void>;
  users?: MentionUser[];
  emptyContent?: React.ReactNode;
  placeholder?: string;
}

export function CommentBox({
  comments,
  addComment,
  removeComment,
  placeholder,
  emptyContent,
  users = [],
}: CommentBoxProps) {
  const t = useTranslations('calendar');
  const { data: session } = useSession();
  const {
    displayText,
    setDisplayText,
    selectedMentions,
    setSelectedMentions,
    textareaHeight,
    setTextareaHeight,
    textareaRef,
    handleTextareaChange,
    handleCursorChange,
    handleKeyDown: handleKeyDownFromHook,
    isVisible,
    position,
    searchQuery,
    selectedIndex,
    setSelectedIndex,
    hideAutocomplete,
    selectUser,
    toStructured,
  } = useMentions({ users });

  const form = useForm<CommentCreateInput>({
    resolver: zodResolver(commentCreateSchema),
    defaultValues: {
      comment: '',
    },
  });

  async function onSubmit(): Promise<void> {
    const structured = toStructured();

    if (!structured || structured.trim() === '') {
      return;
    }

    await addComment({ comment: structured });
    setDisplayText('');
    setSelectedMentions(() => []);
    form.reset();
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.style.height = '40px';
        setTextareaHeight(40);
      }
    }, 0);
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void =>
    handleKeyDownFromHook(e, onSubmit);

  const isCommentEmpty = !displayText || displayText.trim() === '';

  return (
    <div className="flex flex-grow basis-auto flex-col gap-3 pb-4">
      <CommentList comments={comments} onDelete={removeComment} />
      {comments.length === 0 && emptyContent && <>{emptyContent}</>}

      <Form {...form}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
          className="flex basis-[40px] flex-col space-y-4 justify-self-end"
        >
          <FormField
            control={form.control}
            name="comment"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <div className="flex items-start gap-2">
                    <div className="pt-1">
                      <Avatar name={session?.user?.name ?? session?.user?.email ?? 'User'} />
                    </div>
                    <div className="relative flex-1">
                      <div className="relative">
                        <div
                          className="pointer-events-none absolute inset-0 z-10 whitespace-pre-wrap break-words px-3 py-2 text-sm text-white"
                          style={{
                            height: `${textareaHeight}px`,
                            lineHeight: 'inherit',
                          }}
                        >
                          <MentionHighlighter text={displayText} mentions={selectedMentions} />
                        </div>
                        <Textarea
                          {...field}
                          ref={textareaRef}
                          placeholder={placeholder ? placeholder : t('fields.comment')}
                          value={displayText}
                          className="relative z-20 min-h-8 resize-none overflow-hidden bg-transparent text-sm"
                          onKeyDown={handleKeyDown}
                          onChange={handleTextareaChange}
                          onSelect={handleCursorChange}
                          style={{
                            height: `${textareaHeight}px`,
                          }}
                        />
                      </div>
                      <MentionAutocomplete
                        users={users}
                        isVisible={isVisible}
                        position={position}
                        onUserSelect={selectUser}
                        onClose={hideAutocomplete}
                        searchQuery={searchQuery}
                        selectedIndex={selectedIndex}
                        onIndexChange={setSelectedIndex}
                        textareaRef={textareaRef}
                      />
                    </div>
                    <Button
                      type="submit"
                      onClick={(e) => e.stopPropagation()}
                      disabled={isCommentEmpty}
                      className="size-10 border border-input bg-background text-gray-900 shadow-sm hover:bg-accent hover:text-accent-foreground disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Send className="text-gray-500" />
                    </Button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </form>
      </Form>
    </div>
  );
}
